// Masonry — JS-driven left-to-right column distribution.
//
// Why this exists:
//   CSS Columns (`columns-1 sm:columns-2 lg:columns-3`) gives a clean
//   masonry look but fills column-first: item 1 lands above item 2 (same
//   column) instead of beside it. For an editorial gallery where users
//   naturally read left-to-right, that's the wrong sequence — item 2
//   should appear top-middle, item 3 top-right, item 4 below item 1,
//   item 5 below item 2, etc.
//
//   This component distributes items by `index % numColumns` so:
//     1 column  → [1, 2, 3, 4, 5, …]
//     2 columns → [[1, 3, 5, …], [2, 4, 6, …]]
//     3 columns → [[1, 4, 7, …], [2, 5, 8, …], [3, 6, 9, …]]
//
//   Variable per-card heights are preserved (each card retains its
//   intrinsic aspect ratio via natural-mode images). Column heights end
//   up roughly balanced because the distribution is round-robin, but
//   are NOT exactly balanced — that's a feature, not a bug. The Tilda
//   editorial-archive reference (archive.vonstuben.com/artprints) has
//   the same slight column-height variance.
//
// API:
//   <Masonry
//     items={items}
//     breakpoints={{base: 1, sm: 2, lg: 3}}    // optional, this is default
//     gap="gap-6 sm:gap-8"                      // optional Tailwind gap
//     renderItem={(item, originalIndex) => <Card key={item.id} … />}
//     className="…"
//     data-testid="…"
//   />
//
// renderItem MUST return a single element with its OWN key (or a stable
// identity), since each column is a flat `flex-col` mapping.

import React, {useMemo} from 'react';
import {cn} from '@/lib/utils';
import {useResponsiveColumns} from '@/hooks/useResponsiveColumns';

const distributeToColumns = (items, numColumns) => {
    const cols = Array.from({length: numColumns}, () => []);
    items.forEach((item, i) => {
        cols[i % numColumns].push({item, originalIndex: i});
    });
    return cols;
};

export const Masonry = ({
    items,
    breakpoints,
    gap = 'gap-6 sm:gap-8',
    renderItem,
    className,
    testId,
}) => {
    const numColumns = useResponsiveColumns(breakpoints || {base: 1, sm: 2, lg: 3});
    const columns = useMemo(
        () => distributeToColumns(items || [], numColumns),
        [items, numColumns],
    );

    if (!items || items.length === 0) return null;

    return (
        <div
            className={cn('flex w-full', gap, className)}
            data-testid={testId}
            data-masonry-cols={numColumns}
        >
            {columns.map((col, colIdx) => (
                <div
                    key={colIdx}
                    className={cn('flex flex-col flex-1 min-w-0', gap)}
                >
                    {col.map(({item, originalIndex}) =>
                        renderItem(item, originalIndex),
                    )}
                </div>
            ))}
        </div>
    );
};

export default Masonry;
